<template>
  <FloatPanel
    :open="initiateDestructionPanel.state.open"
    :widths="[
      ['(max-width: 768px)', '100vw'],
      ['(min-width: 768px)', '500px']
    ]"
    location="right"
    class="bg-gray-100 md:rounded-tl-2xl md:shadow-xl md:border md:border-gray-400 overflow-y-scroll flex flex-col"
    @click-outside="() => initiateDestructionPanel.close()"
    @esc-pressed="() => initiateDestructionPanel.close()"
  >
    <PanelHeader
      title="Initiate Destruction"
      @close="initiateDestructionPanel.close"
    />

    <form
      @submit.prevent="onSubmit"
      class="px-4 sm:px-8 pb-8 flex flex-col gap-y-4"
    >
      <div class="flex flex-col gap-y-1">
        <div class="flex w-full border-gray-400 items-center border-2 rounded-lg overflow-hidden">
          <label
            for="location_id"
            class="sr-only"
            >Location:</label
          >
          <select
            class="w-5/12 border-none font-semibold focus:ring-0 focus:text-gray-800 overflow-hidden text-ellipsis whitespace-nowrap"
            name="location_id"
            id="location_id"
            v-model="locationId"
            :class="{
              'text-gray-600': !locationId,
              uppercase: locationId
            }"
          >
            <option
              :value="null"
              hidden
            >
              Location
            </option>
            <option
              v-for="location in locations"
              :key="location.id"
              :value="location.id"
            >
              {{ location.name }}
            </option>
          </select>

          <div class="horizontal-divider"></div>

          <label
            for="space_id"
            class="sr-only"
            >Space:</label
          >

          <select
            class="w-7/12 border-none font-semibold focus:ring-0 focus:text-gray-800 overflow-hidden text-ellipsis whitespace-nowrap"
            name="space_id"
            id="space_id"
            :disabled="!locationId"
            v-model="destructionForm.space_id"
            :class="{
              'text-gray-600': !destructionForm.space_id
            }"
          >
            <option
              :value="null"
              hidden
            >
              Space
            </option>
            <option
              v-for="space in spacesForLocation"
              :key="space.id"
              :value="space.id"
            >
              {{ space.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="flex flex-col gap-y-1">
        <div
          class="border-2 border-gray-400 rounded-lg overflow-hidden relative"
          :class="{
            'error-field error-field-group': destructionForm.errors.sub_lot_num
          }"
        >
          <select
            class="w-full border-none font-semibold focus:ring-0 focus:text-gray-800"
            :class="{
              'text-gray-600': !destructionForm.sub_lot_num
            }"
            name="sub_lot_num"
            id="sub_lot_num"
            :disabled="!subLotNums.length"
            v-model="destructionForm.sub_lot_num"
          >
            <option
              :value="null"
              hidden
            >
              Sub-lot
            </option>
            <option
              v-for="subLot in subLotNums"
              :key="subLot"
              :value="subLot"
            >
              {{ subLot }}
            </option>
          </select>
          <span
            v-if="destructionForm.sub_lot_num"
            class="absolute inset-y-0 right-0 pr-10 flex items-center pointer-events-none text-right text-xs font-semibold text-gray-500"
          >
            {{ selectedSubLotQty }} Units
          </span>
        </div>
        <div class="text-xs text-red-400 font-semibold flex items-center justify-between">
          <span v-if="destructionForm.errors.sub_lot_num">{{ destructionForm.errors.sub_lot_num }}</span>
        </div>
      </div>
      <div class="flex flex-col gap-y-1">
        <div
          class="border-2 border-gray-400 rounded-lg overflow-hidden relative"
          :class="{
            'error-field error-field-group': destructionForm.errors.reason
          }"
        >
          <select
            class="w-full border-none font-semibold focus:ring-0 focus:text-gray-800"
            :class="{
              'text-gray-600': !destructionForm.reason
            }"
            name="sub_lot_num"
            id="sub_lot_num"
            :disabled="!reasons.length"
            v-model="destructionForm.reason"
          >
            <option
              :value="null"
              hidden
            >
              Reason
            </option>
            <option
              v-for="reason in reasons"
              :key="reason.id"
              :value="reason.name"
            >
              {{ reason.name }}
            </option>
          </select>
        </div>
        <div class="text-xs text-red-400 font-semibold flex items-center justify-between">
          <span v-if="destructionForm.errors.reason">{{ destructionForm.errors.reason }}</span>
        </div>
      </div>
      <div class="flex flex-col gap-y-1">
        <div class="flex w-full gap-4">
          <button
            :disabled="destructionForm.processing"
            type="submit"
            class="btn btn-primary px-12 font-semibold uppercase"
          >
            Create Destruction
          </button>
        </div>
      </div>
    </form>
  </FloatPanel>
</template>

<style scoped>
.transfer-divider {
  background: linear-gradient(theme('colors.gray.800'), theme('colors.gray.800')) no-repeat center/100% 2px;
}
.horizontal-divider {
  height: calc(100% - 20px);
  width: 1px;
  @apply bg-gray-400;
}
.error-field-group {
  @apply bg-red-200 border-red-400;
}
.error-field {
  select {
    background-color: transparent !important;
    @apply text-red-400;
  }
  input {
    @apply border-red-400 bg-red-200 text-red-400;
  }
  input::placeholder {
    color: theme('colors.red.400') !important;
  }
}
</style>

<script setup>
import initiateDestructionPanel from '@/Pages/Packages/InitiateDestructionPanel'
import { FloatPanel } from '@/Components/Panels'
import PanelHeader from '@/Components/Panels/PanelHeader.vue'
import { useForm } from '@inertiajs/vue3'
import { computed, ref, watch } from 'vue'
import _ from 'lodash'
import { getHeadersWithToken } from '@/utils/http'

const emits = defineEmits(['submitted'])

const props = defineProps({
  locations: Array,
  reasons: Array
})

const locationId = ref(null)
const subLotNums = ref([])

const destructionForm = useForm({
  space_id: null,
  sub_lot_num: null,
  reason: null
})

watch(locationId, () => {
  destructionForm.space_id = null
  destructionForm.reset()
  fetchSubLotOptions()
})

watch(
  () => destructionForm.space_id,
  () => {
    fetchSubLotOptions()
  }
)

const selectedLocation = computed(() => {
  return _.find(props.locations, { id: locationId.value }) || null
})

const spacesForLocation = computed(() => {
  return _.filter(selectedLocation.value?.spaces || [], (space) => space.is_quarantine) || []
})

const selectedSubLotQty = computed(() => {
  return (
    _.find(selectedLocation.value?.packages, {
      display_sub_lot_num: destructionForm.sub_lot_num,
      space_id: destructionForm.space_id
    })?.quantity_on_hand || null
  )
})

const fetchSubLotOptions = async () => {
  if (!destructionForm.space_id) {
    subLotNums.value = []
    return
  }

  const response = await fetch(
    route('api.packages.sub_lot_options', { location_id: locationId.value, space_id: destructionForm.space_id }),
    {
      headers: getHeadersWithToken(),
      method: 'get'
    }
  )

  subLotNums.value = await response.json()
}

const onSubmit = () => {
  destructionForm.post(route('destructions.store'), {
    onSuccess: () => {
      locationId.value = null
      emits('submitted')
      initiateDestructionPanel.close()
    }
  })
}
</script>
